import { useState } from "react";
import { toast } from "sonner";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "../components/ui/select";
import {
  submitCareerGuidanceLead,
  generateCareerPathReport,
  recommendColleges,
  mapBudgetToMaxFee,
  mapGenderToBackend,
} from "../lib/api";
import ReportCard from "./ReportCard";

const STATES = [
  "Andhra Pradesh",
  "Arunachal Pradesh",
  "Assam",
  "Bihar",
  "Chhattisgarh",
  "Goa",
  "Gujarat",
  "Haryana",
  "Himachal Pradesh",
  "Jharkhand",
  "Karnataka",
  "Kerala",
  "Madhya Pradesh",
  "Maharashtra",
  "Manipur",
  "Meghalaya",
  "Mizoram",
  "Nagaland",
  "Odisha",
  "Punjab",
  "Rajasthan",
  "Sikkim",
  "Tamil Nadu",
  "Telangana",
  "Tripura",
  "Uttar Pradesh",
  "Uttarakhand",
  "West Bengal",
  "Andaman and Nicobar Islands",
  "Chandigarh",
  "Dadra and Nagar Haveli and Daman and Diu",
  "Delhi",
  "Jammu and Kashmir",
  "Ladakh",
  "Lakshadweep",
  "Puducherry",
];

const STREAMS = ["MPC", "BiPC", "Diploma"];
const EXAMS = ["TS EAMCET", "AP EAPCET", "JEE"];
const BRANCHES = ["CSE", "ECE", "EEE", "MECH", "CIVIL"];
const GENDERS = [
  { value: "male", label: "Male" },
  { value: "female", label: "Female" },
];
const CASTES = [
  "OC",
  "BC_A",
  "BC_B",
  "BC_C",
  "BC_D",
  "BC_E",
  "SC",
  "ST",
  "EWS",
];
const COLLEGE_PREFS = ["Government", "Private", "Any"];
const BUDGETS = [
  "< ₹50,000 / year",
  "₹50,000 – ₹1 Lakh / year",
  "₹1 Lakh – ₹3 Lakhs / year",
  "₹3 Lakhs+ / year",
];

function SelectField({
  label,
  name,
  value,
  onChange,
  options,
  placeholder,
  error,
  disabled,
  contentClassName,
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-sm font-semibold text-gray-700">{label}</label>
      <Select
        value={value || ""}
        onValueChange={(v) => onChange({ target: { name, value: v } })}
        disabled={disabled}
      >
        <SelectTrigger
          aria-invalid={!!error}
          className={`rounded-xl h-12 text-sm ${
            error ? "border-red-500 focus:ring-red-500" : ""
          } ${!value ? "text-gray-400" : "text-gray-800"}`}
        >
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent className={contentClassName}>
          {options.map((opt) => {
            const optValue = typeof opt === "string" ? opt : opt.value;
            const optLabel = typeof opt === "string" ? opt : opt.label;
            return (
              <SelectItem key={optValue} value={optValue}>
                {optLabel}
              </SelectItem>
            );
          })}
        </SelectContent>
      </Select>
      {error && <p className="text-xs text-red-500 mt-0.5">{error}</p>}
    </div>
  );
}

function InputField({
  label,
  name,
  value,
  onChange,
  placeholder,
  error,
  type = "text",
  disabled,
  readOnly,
}) {
  return (
    <div className="flex flex-col gap-1">
      <label className="text-sm font-semibold text-gray-700">{label}</label>
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        disabled={disabled}
        readOnly={readOnly}
        className={`w-full border ${error ? "border-red-500 focus-visible:ring-red-500" : "border-gray-200"} rounded-xl px-4 h-12 text-sm text-gray-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-aiblue focus:border-transparent transition-all ${disabled || readOnly ? "bg-gray-50 text-gray-500 cursor-not-allowed" : "bg-white"}`}
      />
      {error && <p className="text-xs text-red-500 mt-0.5">{error}</p>}
    </div>
  );
}

const LoadingOverlay = () => (
  <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex flex-col items-center justify-center gap-6">
    <div className="text-center">
        <p className="text-white font-black text-xl mb-2">
          Analysing your profile
        </p>
    </div>
    <div className="relative flex items-center justify-center">
      <div className="w-32 h-32 rounded-full border-4 border-aiblue/30 border-t-aiblue animate-spin absolute" />

      <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center z-10">
        <img
          src="/ai_prof.png"
          alt="AI.Prof"
          className="w-14 h-14 object-contain"
        />
      </div>
    </div>
    <div className="text-center">
      <p className="text-white/80 text-lg font-semibold">
        Generating your career path report and college recommendations
      </p>
    </div>
    <div className="flex gap-2">
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          className="w-2 h-2 rounded-full bg-aiblue animate-bounce"
          style={{ animationDelay: `${i * 0.15}s` }}
        />
      ))}
    </div>
  </div>
);

export default function CareerGuidance() {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [report, setReport] = useState(null);
  const [colleges, setColleges] = useState(null);
  const [formData, setFormData] = useState({
    fullName: "",
    mobile: "",
    email: "",
    country: "India",
    state: "",
    city: "",
    gender: "",
    stream: "",
    exam: "",
    rank: "",
    caste: "",
    preferredBranch: "",
    collegeType: "",
    budget: "",
  });
  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.fullName.trim())
      newErrors.fullName = "Full name is required.";
    if (!formData.mobile.match(/^[6-9]\d{9}$/))
      newErrors.mobile = "Enter a valid 10-digit mobile number.";
    if (!formData.email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/))
      newErrors.email = "Enter a valid email address.";
    if (!formData.state) newErrors.state = "Please select your state.";
    if (!formData.city.trim())
      newErrors.city = "Please enter your district / city.";
    if (!formData.gender) newErrors.gender = "Please select your gender.";
    if (!formData.stream) newErrors.stream = "Please select your stream.";
    if (!formData.exam) newErrors.exam = "Please select your exam type.";
    if (!formData.rank.trim()) newErrors.rank = "Rank is required.";
    else if (!/^\d+$/.test(formData.rank.trim()))
      newErrors.rank = "Rank must be a number.";
    if (!formData.caste) newErrors.caste = "Please select your caste category.";
    if (!formData.preferredBranch)
      newErrors.preferredBranch = "Please select preferred branch.";
    if (!formData.collegeType)
      newErrors.collegeType = "Please select college preference.";
    if (!formData.budget) newErrors.budget = "Please select your budget.";
    return newErrors;
  };

  const handleSubmit = async () => {
    const newErrors = validate();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      const firstKey = Object.keys(newErrors)[0];
      const messages = {
        fullName: "Please enter your full name.",
        mobile: "Enter a valid 10-digit mobile number.",
        email: "Enter a valid email address.",
        state: "Please select your state.",
        city: "Please enter your district / city.",
        gender: "Please select your gender.",
        stream: "Please select your stream.",
        exam: "Please select your exam type.",
        rank: "Please enter your exam rank.",
        caste: "Please select your caste category.",
        preferredBranch: "Please select your preferred branch.",
        collegeType: "Please select your college preference.",
        budget: "Please select your budget range.",
      };
      toast.error(messages[firstKey] || "Please fix the errors in the form.");
      const firstError = document.querySelector('[data-error="true"]');
      if (firstError)
        firstError.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }

    setSubmitting(true);
    const payload = {
      full_name: formData.fullName.trim(),
      mobile_number: formData.mobile.trim(),
      email: formData.email.trim().toLowerCase(),
      country: formData.country,
      state: formData.state,
      city: formData.city.trim(),
      gender: formData.gender,
      stream: formData.stream,
      exam: formData.exam,
      rank: formData.rank.trim(),
      caste: formData.caste,
      preferred_branch: formData.preferredBranch,
      college_type: formData.collegeType,
      budget: formData.budget,
    };

    const collegePayload = {
      rank: parseInt(formData.rank.trim(), 10),
      category: formData.caste,
      gender: mapGenderToBackend(formData.gender),
      location: formData.city.trim(),
      budget: mapBudgetToMaxFee(formData.budget),
      preferred_branch: formData.preferredBranch.toLowerCase(),
    };

    try {
      await submitCareerGuidanceLead(payload);
      const [reportResult, collegeResult] = await Promise.all([
        generateCareerPathReport(payload),
        recommendColleges(collegePayload),
      ]);
      setReport(reportResult.report);
      setColleges(collegeResult.data);
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(err.message || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted && report)
    return <ReportCard data={formData} report={report} colleges={colleges} />;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 py-12 px-4">
      {submitting && <LoadingOverlay />}
      <div className="max-w-5xl mx-auto">
        <div className="text-center mt-20 mb-10">
          <span className="inline-block bg-aiorange/10 text-aiorange text-xs font-bold uppercase tracking-widest px-4 py-1.5 rounded-full mb-4">
            Free Career Guidance
          </span>
          <h1 className="text-6xl font-black text-gray-900 leading-none mb-3">
            Find Your Perfect
            <br />
            <span className="text-aiblue"> Engineering Path</span>
          </h1>
          <p className="text-gray-500 text-base mx-auto">
            Answer a few questions and get a personalised career roadmap from
            AI.Prof's guidance engine.
          </p>
        </div>

        <div className="bg-white rounded-3xl shadow-xl border border-gray-100 overflow-hidden">
          <div className="px-8 pt-8 pb-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-8 h-8 rounded-xl bg-aiblue flex items-center justify-center flex-shrink-0">
                <svg
                  className="w-4 h-4 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                  />
                </svg>
              </div>
              <h2 className="font-black text-gray-900 text-lg">
                Personal Details
              </h2>
            </div>

            <div className="grid gap-5">
              <div data-error={!!errors.fullName}>
                <InputField
                  label="Full Name *"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleChange}
                  placeholder="Enter your full name"
                  error={errors.fullName}
                />
              </div>
              <div className="grid sm:grid-cols-2 gap-5">
                <div data-error={!!errors.mobile}>
                  <InputField
                    label="Mobile Number *"
                    name="mobile"
                    value={formData.mobile}
                    onChange={handleChange}
                    placeholder="10-digit mobile number"
                    type="tel"
                    error={errors.mobile}
                  />
                </div>
                <div data-error={!!errors.email}>
                  <InputField
                    label="Email Address *"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="you@email.com"
                    type="email"
                    error={errors.email}
                  />
                </div>
              </div>
              <div className="grid sm:grid-cols-3 gap-5">
                <div>
                  <InputField
                    label="Country"
                    name="country"
                    value="India"
                    readOnly
                  />
                </div>
                <div data-error={!!errors.state}>
                  <SelectField
                    label="State *"
                    name="state"
                    value={formData.state}
                    onChange={handleChange}
                    options={STATES}
                    placeholder="Select state"
                    error={errors.state}
                    contentClassName="max-h-60"
                  />
                </div>
                <div data-error={!!errors.city}>
                  <InputField
                    label="District / City *"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    placeholder="Your city"
                    error={errors.city}
                  />
                </div>
              </div>
              <div data-error={!!errors.gender}>
                <SelectField
                  label="Gender *"
                  name="gender"
                  value={formData.gender}
                  onChange={handleChange}
                  options={GENDERS}
                  placeholder="Select gender"
                  error={errors.gender}
                />
              </div>
            </div>
          </div>

          <div className="h-px bg-gradient-to-r from-transparent via-aiblue/20 to-transparent mx-8" />

          <div className="px-8 py-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-8 h-8 rounded-xl bg-aiorange flex items-center justify-center flex-shrink-0">
                <svg
                  className="w-4 h-4 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                  />
                </svg>
              </div>
              <h2 className="font-black text-gray-900 text-lg">
                Academic Background
              </h2>
            </div>

            <div className="grid sm:grid-cols-2 gap-5">
              <div data-error={!!errors.stream}>
                <SelectField
                  label="Stream *"
                  name="stream"
                  value={formData.stream}
                  onChange={handleChange}
                  options={STREAMS}
                  placeholder="Select stream"
                  error={errors.stream}
                />
              </div>
              <div data-error={!!errors.exam}>
                <SelectField
                  label="Exam Type *"
                  name="exam"
                  value={formData.exam}
                  onChange={handleChange}
                  options={EXAMS}
                  placeholder="Select exam"
                  error={errors.exam}
                />
              </div>
              <div data-error={!!errors.rank}>
                <InputField
                  label="Rank *"
                  name="rank"
                  value={formData.rank}
                  onChange={handleChange}
                  placeholder="Enter your exam rank"
                  error={errors.rank}
                />
              </div>
              <div data-error={!!errors.caste}>
                <SelectField
                  label="Caste Category *"
                  name="caste"
                  value={formData.caste}
                  onChange={handleChange}
                  options={CASTES}
                  placeholder="Select caste category"
                  error={errors.caste}
                />
              </div>
            </div>
          </div>

          <div className="h-px bg-gradient-to-r from-transparent via-aiblue/20 to-transparent mx-8" />

          <div className="px-8 py-6 pb-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-8 h-8 rounded-xl bg-indigo-600 flex items-center justify-center flex-shrink-0">
                <svg
                  className="w-4 h-4 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"
                  />
                </svg>
              </div>
              <h2 className="font-black text-gray-900 text-lg">
                Your Preferences
              </h2>
            </div>

            <div className="grid sm:grid-cols-2 gap-5">
              <div data-error={!!errors.preferredBranch}>
                <SelectField
                  label="Preferred Branch *"
                  name="preferredBranch"
                  value={formData.preferredBranch}
                  onChange={handleChange}
                  options={BRANCHES}
                  placeholder="Select branch"
                  error={errors.preferredBranch}
                />
              </div>
              <div data-error={!!errors.collegeType}>
                <SelectField
                  label="Preferred College Type *"
                  name="collegeType"
                  value={formData.collegeType}
                  onChange={handleChange}
                  options={COLLEGE_PREFS}
                  placeholder="Select type"
                  error={errors.collegeType}
                />
              </div>
              <div className="sm:col-span-2" data-error={!!errors.budget}>
                <SelectField
                  label="Budget Per Year *"
                  name="budget"
                  value={formData.budget}
                  onChange={handleChange}
                  options={BUDGETS}
                  placeholder="Select budget range"
                  error={errors.budget}
                />
              </div>
            </div>
          </div>

          <div className="bg-gray-50 px-8 py-6 border-t border-gray-100">
            <button
              onClick={handleSubmit}
              disabled={submitting}
              className="w-full bg-aiblue hover:bg-aiblue/90 active:scale-[0.98] disabled:opacity-60 disabled:cursor-not-allowed text-white font-semibold py-6 px-8 rounded-full text-base transition-all shadow-lg hover:shadow-xl flex items-center justify-center gap-2 group cta-pulse"
            >
              {submitting
                ? "Generating Report..."
                : "Get My Career Path Report"}
              <svg
                className="w-5 h-5 group-hover:translate-x-1 transition-transform"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 7l5 5m0 0l-5 5m5-5H6"
                />
              </svg>
            </button>
            <p className="text-center text-xs text-gray-400 mt-3">
              Free · No spam · Our team will reach out with personalized
              guidance
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
